// @flow
// $FlowFixMe - Cannot import useState because there is no useState export in react.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import { TextBlock, Text, Button, toastError } from 'bench-internal-theme';
import AddonsTabHeader from './AddonsTabHeader';
import AddonsTabRowContainer from './AddonsTabRowContainer';
import type { Addon } from '../types';
import { useDrop } from 'react-dnd';
import { useClient } from '../../ClientContext';
import { useMutation } from '@apollo/client';
import { GET_ADDONS, REORDER_ADDON } from '../queries';
import EmptyTableRow from '../EmptyTableRow';
import { showServerError } from 'bk-web-app';
import { compareAsNumbers } from 'comparators';

const AddonsTabLayout = styled.div`
  bottom: 0;
  left: 0;
  overflow: auto;
  padding: 32px;
  position: absolute;
  right: 0;
  top: 0;
`;

const AddonsTabEmpty = styled(TextBlock)`
  align-items: start;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.silt};
  box-shadow: 0 -1px 0 ${colors.silt};
  margin: 0 32px;
  padding: 8px;
  position: relative;
`;

const AddonsTableLayout = styled.div`
  margin: 0 -32px;
  max-width: 1700px;
  padding: 24px 0;
`;

const SortButtonLayout = styled.div`
  position: absolute;
  top: -8px;
  right: 0;
  padding: 32px 32px 12px 32px;
`;

type AddonsTabProps = {
  addons: Array<Addon>,
  search: string,
  isSorting: boolean,
  clearFiltersForSorting: () => void,
  canSort: boolean,
};

const AddonsTab = ({
  addons,
  search,
  isSorting,
  clearFiltersForSorting,
  canSort,
}: AddonsTabProps) => {
  const client = useClient();
  const [reorderAddon, { loading }] = useMutation(REORDER_ADDON, {
    onError: error => {
      toastError('An error occurred reordering this add-on', showServerError(error));
    },
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_ADDONS, variables: { clientId: client.id } }],
  });

  const changeOrder = (addonId, reorderId, isLast = false) => {
    return reorderAddon({ variables: { addonId, reorderId, isLast } });
  };

  const sortedAddons = Array.from(addons.values()).sort((addonA, addonB) =>
    compareAsNumbers(addonA.order, addonB.order),
  );

  const lastAddonId = sortedAddons[sortedAddons.length - 1]?.id;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'Add-on',
      canDrop: () => isSorting && canSort,
      drop: monitor => changeOrder(monitor.id, lastAddonId, true),
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [lastAddonId, isSorting, canSort],
  );

  return (
    <AddonsTabLayout>
      <SortButtonLayout>
        {canSort && !isSorting && (
          <Button disabled={isSorting} styleType="secondary" onClick={clearFiltersForSorting}>
            Sort Add-ons
          </Button>
        )}
      </SortButtonLayout>
      <TextBlock styleType="largeBold">{`Add-ons (${sortedAddons?.length})`}</TextBlock>
      <AddonsTableLayout>
        <AddonsTabHeader />
        {sortedAddons.length ? (
          <>
            {sortedAddons.map(addon => (
              <AddonsTabRowContainer
                addon={addon}
                key={addon.id}
                search={search}
                isSortSaving={loading}
                isSorting={isSorting}
                canSort={canSort}
                changeOrder={changeOrder}
              />
            ))}
            {/* Allows user to drop row at the bottom of the table to sort last */}
            {isSorting && canSort && canDrop && <EmptyTableRow ref={drop} isOver={isOver} />}
          </>
        ) : (
          <AddonsTabEmpty>
            <Text styleType="small" color={colors.cloud}>
              No add-ons found for this client
            </Text>
          </AddonsTabEmpty>
        )}
      </AddonsTableLayout>
    </AddonsTabLayout>
  );
};

export default AddonsTab;
